<script lang="ts" setup>
import { formatDateTimeRelative, ProfileImageComponent } from '@metapic-apps/shared'
import DOMPurify from 'dompurify'
import { computed } from 'vue'

import { exhaustiveGuard } from '@/lib/helpers'
import { NotificationActionType, NotificationStatus, type NotificationType, STATUS_TITLE_MAP } from '@/lib/types/notifications.types'
import { BoxComponent, FlexComponent, TypographyComponent } from '@/ui-library'

const props = defineProps<{
  notification: NotificationType
}>()
const emit = defineEmits<(e: 'toggle-status') => void>()
const payload = computed(() => props.notification.data.events[0]!.payload)
const isGrouped = computed(() => {
  return props.notification.data.events.length > 1
})
const secondPayload = computed(() => {
  if (isGrouped.value) {
    return props.notification.data.events[1]?.payload
  }
  return null
})
const statusText = (status: NotificationStatus) => {
  const text = STATUS_TITLE_MAP[status]
  return text ?? status
}

const actionLabel = computed(() => {
  if (isGrouped.value) {
    switch (payload.value.action.type) {
      case NotificationActionType.CommentCreate:
        return `New ${props.notification.data.events.length} comments on ${payload.value.participant.name}`
      case NotificationActionType.StatusUpdate:
        return `${props.notification.data.events.length} creators where moved`
      case NotificationActionType.ParticipantAdded:
        return `${props.notification.data.events.length} creators where invited`
      default:
        return exhaustiveGuard(payload.value.action)
    }
  }

  const name = payload.value.participant.name

  switch (payload.value.action.type) {
    case NotificationActionType.CommentCreate:
      return `New comment on ${name}`
    case NotificationActionType.StatusUpdate: {
      if (payload.value.action.oldStatus && payload.value.action.newStatus) {
        return `${name} was moved`
      } else if (payload.value.action.newStatus === NotificationStatus.OPEN) {
        return `${name} was invited`
      } else {
        return payload.value.action.newStatus
      }
    }
    case NotificationActionType.ParticipantAdded:
      return `${name} was invited`
    default:
      return exhaustiveGuard(payload.value.action)
  }
})
const sanitizedCommentText = computed(() => {
  if (payload.value?.action?.type === NotificationActionType.CommentCreate) {
    return DOMPurify.sanitize(payload.value.action.commentText)
  }
  return null
})
</script>
<template>
  <div class="notification-list-item">
    <FlexComponent :gap="8" align="flex-start" class="notification-list-item__container">
      <BoxComponent v-if="isGrouped" class="notification-list-item__grouped-avatars">
        <ProfileImageComponent
          :image="payload.participant.avatar"
          :name="payload.participant.name"
          :alt="payload.participant.name"
          :size="24"
        />
        <ProfileImageComponent
          v-if="secondPayload"
          :image="secondPayload.participant.avatar"
          :name="secondPayload.participant.name"
          :alt="secondPayload.participant.name"
          :size="24"
        />
      </BoxComponent>
      <ProfileImageComponent
        v-else
        :image="payload.participant.avatar"
        :name="payload.participant.name"
        :alt="payload.participant.name"
        :size="32"
      />
      <FlexComponent direction="column" class="notification-list-item__content">
        <TypographyComponent variant="title-medium">
          {{ actionLabel }}
          <TypographyComponent is="span" variant="title-medium" color="#031E517A">
            {{ formatDateTimeRelative(notification.createdAt) }}
          </TypographyComponent>
        </TypographyComponent>
        <TypographyComponent variant="body-small" class="payload-list-item__campaign">
          {{ payload.advertiser?.name }} • {{ payload.campaign.name }}
        </TypographyComponent>
        <TypographyComponent variant="body-small" color="#031E517A" class="notification-list-item__author">
          {{ payload.actor.name }}
        </TypographyComponent>
      </FlexComponent>

      <el-tooltip :disabled="notification.read" content="Mark as read" placement="left" effect="light">
        <div
          :class="['notification-list-item__indicator', { 'notification-list-item__indicator--unread': !notification.read }]"
          @click.stop="emit('toggle-status')"
        />
      </el-tooltip>
    </FlexComponent>
    <TypographyComponent
      is="div"
      v-if="payload.action.type === NotificationActionType.StatusUpdate && payload.action.oldStatus && payload.action.newStatus"
      class="notification-list-item__border"
      color="#031E517A"
      variant="body-small"
    >
      {{ statusText(payload.action.oldStatus) }} → {{ statusText(payload.action.newStatus) }}
    </TypographyComponent>
    <div v-if="payload.action.type === NotificationActionType.CommentCreate" class="notification-list-item__border">
      <TypographyComponent is="div" variant="body-small" color="#031E517A" class="notification-list-item__comment">
        <!-- eslint-disable vue/no-v-html -->
        <div class="notification-list-item__comment-content" @click.prevent v-html="sanitizedCommentText"></div>
      </TypographyComponent>
    </div>
  </div>
</template>

<style scoped lang="scss">
.notification-list-item {
  cursor: pointer;
  padding: 8px 16px;
  position: relative;

  &:hover {
    background-color: #f9fafb;
  }
}

.notification-list-item__content {
  flex: 1;
  overflow: hidden;
}

.notification-list-item__border {
  margin: 8px 8px 0 40px;
  border: 1px solid #e5e7eb;
  padding: 8px;
  border-radius: 8px;
}
.notification-list-item__comment {
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  text-overflow: ellipsis;
  cursor: pointer;
}

.notification-list-item__expand-toggle {
  margin-top: 4px;
  font-size: 12px;
  color: #031e51;
  cursor: pointer;
  text-align: right;

  &:hover {
    text-decoration: underline;
  }
}

.notification-list-item {
  &__indicator {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    border: 1px solid transparent;
    transition: all 0.2s ease;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;

    &--unread {
      background-color: transparent;

      &::before {
        content: '';
        width: 8px;
        height: 8px;
        background-color: #ea4f85;
        border-radius: 50%;
        display: block;
      }

      &:hover {
        border: 1px solid rgba(3, 30, 81, 0.08);
        background: rgba(3, 30, 81, 0.04);
      }
    }
  }
}
.notification-list-item__grouped-avatars {
  position: relative;
  padding: 4px;

  > :nth-child(even) {
    position: absolute;
    z-index: 1;
    top: 4px;
    right: 0;
  }
}
:deep(.notification-list-item__comment-content) {
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  text-overflow: ellipsis;

  a {
    text-decoration: underline;
  }

  p,
  ul,
  ol {
    margin-bottom: 15px;
    padding-bottom: 1px;
  }

  p:last-child,
  ul:last-child,
  ol:last-child {
    margin-bottom: 0;
    padding-bottom: 0;
  }
}
</style>
